import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { z } from 'zod';

import Input from 'components/Input/Input';
import { cn } from 'components/lib/utils';

import InputSelectDropdown from '../../../../../../components/InputSelectDropdown/InputSelectDropdown';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../components/ui/form';
import {
  EContentType,
  ERROR_FIELD_CLASS,
  IBlockContentProps
} from '../../../constants';
import {
  contentsPreviewBlockSchema,
  pageFormSchema
} from '../../../validation-schema';

type ContentsPreviewBlockType = z.infer<typeof contentsPreviewBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

// Helper type for contents preview block field render props
type ContentsPreviewFieldProps<T extends keyof ContentsPreviewBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: ContentsPreviewBlockType[T];
  };

const contentTypeOptions = [
  {
    label: 'News',
    value: EContentType.NEWS
  },
  {
    label: 'Article',
    value: EContentType.ARTICLE
  }
];

export const ContentsPreviewForm: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const { control, watch, getFieldState } =
    useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}` as const;
  const currentContentType = watch(`${basePath}.contentType`);

  const getFieldError = (fieldName: string) => {
    const fieldState = getFieldState(fieldName as any);
    return fieldState.error;
  };

  return (
    <div
      className={
        'flex flex-col gap-4 p-4 rounded-md border transition-colors border-base-border'
      }
    >
      <FormField
        control={control}
        name={`${basePath}.contentType`}
        render={({
          field: { onChange }
        }: {
          field: ContentsPreviewFieldProps<'contentType'>;
        }) => (
          <FormItem>
            <FormLabel>* Content Type</FormLabel>
            <FormControl>
              <InputSelectDropdown
                label=""
                defaultSelected={
                  contentTypeOptions.find(
                    (option) =>
                      option.value === (currentContentType || EContentType.NEWS)
                  ) || contentTypeOptions[0]
                }
                options={contentTypeOptions}
                onOptionChange={onChange}
                placeholder="Select content type"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.title`}
        render={({ field }: { field: ContentsPreviewFieldProps<'title'> }) => (
          <FormItem>
            <FormLabel>* Title</FormLabel>
            <FormControl>
              <Input
                isRequired={true}
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter title"
                className={cn(
                  getFieldError(`${basePath}.title`)?.message
                    ? ERROR_FIELD_CLASS
                    : ''
                )}
                state={
                  getFieldError(`${basePath}.title`)?.message
                    ? 'error'
                    : 'default'
                }
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.maxItems`}
        render={({
          field
        }: {
          field: ContentsPreviewFieldProps<'maxItems'>;
        }) => (
          <FormItem>
            <FormLabel>* Max Items</FormLabel>
            <FormControl>
              <Input
                type="number"
                placeholder="Enter max items"
                {...field}
                min={1}
                max={50}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  field.onChange(value === '' ? undefined : Number(value));
                }}
                value={field.value ?? ''}
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
    </div>
  );
};
